import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Dropdown,
  Empty,
  Switch,
  Table,
  Tooltip,
  Typography,
  message,
} from "antd";
import { ReloadOutlined, MoreOutlined } from "@ant-design/icons";
import { formatDayHeading } from "./RecommendedSchedulePlanner.jsx";
import { LoadingIcon } from "../../../components/index.js";
import { BACKEND_DATE_FORMAT, COLORS } from "../../../consts/index.js";
import dayjs from "dayjs";
import { UserContext } from "../../../context/UserContext.jsx";
import {
  selectTargetsFromCell,
  markQuick,
  unharvest,
} from "./harvestUpdate.service.js";
import HarvestUpdateModal from "./HarvestUpdateModal.jsx";

/** @typedef {import("./harvestOptimizer.types.d.ts").RecommendedScheduleViewProps} RecommendedScheduleViewProps */

const { Text } = Typography;

const addOrdinalSuffix = (num) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const value = num % 100;
  return `${num}${suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]}`;
};

const formatFullHeader = (dateObj) =>
  `${dateObj.format("dddd")} (${addOrdinalSuffix(dateObj.date())} ${dateObj.format("MMMM")})`;

const startOfWeekMonday = (d) =>
  d.subtract((d.day() + 6) % 7, "day").startOf("day");

const buildSelectedDays = (startISO, endISO, visibleDays) => {
  const wanted = new Set((visibleDays || []).map((n) => n.toLowerCase()));
  const start = dayjs(startISO, BACKEND_DATE_FORMAT, true).startOf("day");
  const end = dayjs(endISO, BACKEND_DATE_FORMAT, true).startOf("day");
  if (!start.isValid() || !end.isValid()) return [];

  const out = [];
  for (let cur = start; !cur.isAfter(end, "day"); cur = cur.add(1, "day")) {
    const dayName = cur.format("dddd");
    if (!wanted.has(dayName.toLowerCase())) continue;

    out.push({
      iso: cur.format(BACKEND_DATE_FORMAT),
      title: formatDayHeading(cur),
      dayName,
      dateObj: cur,
    });
  }
  return out;
};

const compressRowIndices = (rows) => {
  if (!rows?.length) return "";
  const unique = [...new Set(rows)]
    .filter(Number.isFinite)
    .sort((a, b) => a - b);

  const segments = [];
  let start = unique[0];
  let previous = unique[0];

  const pushSegment = (a, b) => {
    const span = b - a + 1;
    if (span >= 3) segments.push(`${a}-${b}`);
    else if (span === 2) segments.push(`${a},${b}`);
    else segments.push(String(a));
  };

  for (let index = 1; index < unique.length; index++) {
    const value = unique[index];
    if (value === previous + 1) {
      previous = value;
      continue;
    }
    pushSegment(start, previous);
    start = value;
    previous = value;
  }
  pushSegment(start, previous);
  return segments.join(",");
};

const expandCompressedRows = (rowName) => {
  if (rowName == null) return [];
  // if number, return as single-element array
  if (Number.isFinite(rowName)) return [Number(rowName)];

  const parts = String(rowName)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  const rows = [];
  for (const part of parts) {
    const match = part.match(/^(\d+)\s*[-–]\s*(\d+)$/);
    if (match) {
      const start = Number(match[1]);
      const end = Number(match[2]);
      for (let v = Math.min(start, end); v <= Math.max(start, end); v++)
        rows.push(v);
    } else if (/^\d+$/.test(part)) {
      rows.push(Number(part));
    }
  }
  return rows;
};

function readHarvestConfig(siteMetadata) {
  const cfg = siteMetadata?.HARVEST_OPTIMIZER || {};
  return {
    yieldPerM: Number(cfg.YIELD_PER_METRE_KG_PER_M) || 0,
    weightPerCrate: Number(cfg.WEIGHT_PER_CRATE_KG) || 0,
    cratesPerPallet: Number(cfg.CRATES_PER_PALLET) || 0,
    minLeafCm: Number(siteMetadata?.MIN_IDEAL_HARVEST_LEAF_SIZE) || 0,
    maxLeafCm: Number(siteMetadata?.MAX_IDEAL_HARVEST_LEAF_SIZE) || 0,
  };
}

function getLeafStageColor(avgLeafCm, minLeafCm, maxLeafCm) {
  // sets default text color (black)
  if (!Number.isFinite(avgLeafCm)) return undefined;
  
  // exceeding spec size 
  if (maxLeafCm > 0 && avgLeafCm > maxLeafCm) return COLORS.dangerRed;
  
  // within the spec size 
  if (minLeafCm > 0 && avgLeafCm >= minLeafCm) return COLORS.successGreen;
  
  // mid-stage
  if (avgLeafCm >= 5) return COLORS.primaryOrange;
  
  // Early-mid stage
  if (avgLeafCm < 5) return COLORS.primaryBlue;
}

const plural = (base, n) => (Number(n) === 1 ? base : `${base}s`);
const fmtInt = (x) => (Number.isFinite(x) ? String(Math.round(x)) : "—");
const fmt1 = (x) =>
  Number.isFinite(x)
    ? (Math.round(x * 10) / 10).toFixed(x % 1 === 0 ? 0 : 1)
    : "—";

function getRequirementForDay(visibleDaysConfig, dayName) {
  if (!visibleDaysConfig) return null;
  const lc = String(dayName || "").toLowerCase();
  let entry = null;
  for (const [k, v] of Object.entries(visibleDaysConfig)) {
    if (String(k).toLowerCase() === lc) {
      entry = v;
      break;
    }
  }
  if (entry == null) return null;
  if (typeof entry === "number") return { type: "row", value: Number(entry) };
  const t = String(entry?.type ?? "").toLowerCase();
  const val = Number(entry?.value);
  if (!t || !Number.isFinite(val)) return null;
  return { type: t, value: val };
}

function formatRequirementText(req) {
  if (!req) return "No requirement configured";
  const { type, value } = req;
  if (type === "row") return `${fmtInt(value)} ${plural("Row", value)}`;
  if (type === "crate") return `${fmtInt(value)} ${plural("Crate", value)}`;
  if (type === "pallet") return `${fmtInt(value)} ${plural("Pallet", value)}`;
  if (type === "total_yield") return `${fmt1(value)} kg Total Yield`;
  return "No requirement configured";
}

function buildCellTooltip(meta) {
  const lenTxt = Number.isFinite(meta?.lengthM)
    ? `${fmt1(meta.lengthM)} m`
    : "—";
  const avgTxt = Number.isFinite(meta?.avgLeafCm)
    ? `${fmt1(meta.avgLeafCm)} cm`
    : "—";
  const kgTxt = Number.isFinite(meta?.kg) ? `${fmt1(meta.kg)} kg` : "—";
  const cratesTxt = Number.isFinite(meta?.crates) ? fmtInt(meta.crates) : "—";
  return (
    <div>
      <div>Row Length: {lenTxt}</div>
      <div>Average Leaf Size: {avgTxt}</div>
      <div>Estimated Yield: {kgTxt}</div>
      <div>Estimated Crates: {cratesTxt}</div>
    </div>
  );
}

/**
 * Try to produce a per-row label from an item's rowLabel.
 * If the label contains a number or a range, replace it with the concrete row number.
 * Fallback to "Row {n}" if nothing useful is found.
 */
function labelForRow(baseLabel, rowNumber) {
  const n = Number(rowNumber);
  if (!baseLabel || typeof baseLabel !== "string") return `Row ${n}`;
  const replaced = baseLabel.replace(/\d+(\s*[-–]\s*\d+)?/, String(n));
  return replaced || `Row ${n}`;
}

/** @param {RecommendedScheduleViewProps & { siteMetadata?: any }} props */
const RecommendedScheduleView = ({
  currentOrgId,
  currentSite,
  currentCrop,
  selectedDate,
  onDateChange,
  loading,
  error,
  items,
  baseDays,
  optionalDays,
  visibleDaysConfig,
  greenHouseLabel,
  blockLabel,
  onRangeChange,
  onRefresh,
  refreshLoading = false,
  siteMetadata,
}) => {
  const FORECAST_DAYS = 7;

  // Merge toggle as before
  const [mergeRows, setMergeRows] = useState(true);
  const [showVariety, setShowVariety] = useState(false);
  const { userData } = useContext(UserContext);

  const { yieldPerM, weightPerCrate, minLeafCm, maxLeafCm } = useMemo(
    () => readHarvestConfig(siteMetadata),
    [siteMetadata],
  );

  // Demo mode (default ON)
  const [demoMode, setDemoMode] = useState(false);

  // Real date shown in the UI (kept separate from the internal Monday date)
  const [uiDate, setUiDate] = useState(selectedDate);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTargets, setModalTargets] = useState([]);

  useEffect(() => {
    if (!demoMode) {
      // When demo mode is off, UI follows parent date exactly.
      setUiDate(selectedDate);
      return;
    }
    // In demo mode, parent should use Monday-of-week for computations.
    const monday = startOfWeekMonday(uiDate || selectedDate);
    if (!selectedDate.isSame(monday, "day")) onDateChange(monday);
    // do not add selectedDate/uiDate to deps to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode]);

  useEffect(() => {
    if (!userData?.metadata) return;
    if (userData?.metadata?.HARVEST_OPTIMIZER?.DEMO_MODE === true) {
      setDemoMode(true);
    }
  }, [userData]);
  // Columns are built from the parent's date (which is Monday in demo mode)
  const showDayNames = useMemo(() => {
    const isValidEntry = (e) =>
      !!e &&
      typeof e === "object" &&
      e.type != null &&
      e.value != null &&
      Number.isFinite(Number(e.value));

    const lc = (s) => String(s || "").toLowerCase();
    const cfgByName = Object.fromEntries(
      Object.keys(visibleDaysConfig || {}).map((k) => [
        lc(k),
        visibleDaysConfig[k],
      ]),
    );

    const validOptional = (optionalDays || []).filter((d) =>
      isValidEntry(cfgByName[lc(d)]),
    );

    const names = [...(baseDays || []), ...validOptional];

    // ALWAYS include Friday for consistency (even if not configured / no data)
    if (!names.some((d) => lc(d) === "friday")) names.push("Friday");

    return names;
  }, [baseDays, optionalDays, visibleDaysConfig]);

  const daysCandidate = useMemo(() => {
    const monday = startOfWeekMonday(selectedDate);
    const startISO = monday.format(BACKEND_DATE_FORMAT);

    const thisWeekSunday = monday.add(6, "day");
    const endISO = thisWeekSunday.format(BACKEND_DATE_FORMAT);

    return buildSelectedDays(startISO, endISO, showDayNames);
  }, [selectedDate, showDayNames]);

  const lastRangeRef = useRef({ startISO: null, endISO: null });

  useEffect(() => {
    const monday = startOfWeekMonday(selectedDate);
    const startISO = monday.format(BACKEND_DATE_FORMAT);

    const thisWeekSunday = monday.add(6, "day");
    const endISO = thisWeekSunday.format(BACKEND_DATE_FORMAT);

    const next = { startISO, endISO };
    const prev = lastRangeRef.current;

    if (prev.startISO !== next.startISO || prev.endISO !== next.endISO) {
      lastRangeRef.current = next;
      onRangeChange(next);
    }
  }, [selectedDate, onRangeChange]);

  const itemsByDate = useMemo(() => {
    const m = new Map();
    for (const it of items || []) {
      const key = it.recommendedHarvestDate;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(it);
    }
    return m;
  }, [items]);

  // 4) Final days to render: keep all baseDays; keep optional only if there are items for that day
  const days = useMemo(() => {
    const lc = (s) => String(s || "").toLowerCase();
    const optionalSet = new Set((optionalDays || []).map((d) => lc(d)));

    return daysCandidate.filter((d) => {
      const dayLc = lc(d.dayName);
      const isOptional = optionalSet.has(dayLc);

      // Keep Friday always, even if optional and empty
      if (dayLc === "friday") return true;

      if (!isOptional) return true;

      const count = (itemsByDate.get(d.iso) || []).length;
      return count > 0;
    });
  }, [daysCandidate, optionalDays, itemsByDate]);

  /**
   * Build cell values + meta.
   * Sorting rules updated per your request:
   * - mergeRows === false: NO sorting anywhere (preserve original item order)
   * - mergeRows === true: preserve bay order by first appearance (NO bay sort),
   *   but keep row-level sort as-is.
   */
  const cellValuesByDay = useMemo(() => {
    const result = {};
    for (const dayInfo of days) {
      const daily = (items || []).filter(
        (it) => it.recommendedHarvestDate === dayInfo.iso,
      );

      if (mergeRows) {
        // Per-bay aggregations (bay order preserved by first appearance in `daily`)
        const rowsByBay = new Map(); // bay -> Set<number>
        const rowHarvestedByBay = new Map(); // bay -> Map<number, boolean> (AND across items)
        const rowLabelByBay = new Map(); // bay -> Map<number, string>   (first non-empty wins)
        const harvestedByBay = new Map(); // bay -> boolean (AND)
        const varietySetByBay = new Map(); // bay -> Set<string>
        const accByBay = new Map(); // bay -> { lengthM, kg, avgSum, avgCnt }

        const accByBayStatus = new Map(); // bay -> { harvested: acc, unharvested: acc }
        const varietyByBayStatus = new Map(); // bay -> { harvested: Set, unharvested: Set }

        const firstSeenIndexByBayStatus = new Map(); // bay -> { harvested: number|null, unharvested: number|null }
        let segmentTieBreaker = 0;

        const ensureAccPair = (bay) => {
          if (!accByBayStatus.has(bay)) {
            accByBayStatus.set(bay, {
              harvested: { lengthM: 0, kg: 0, avgSum: 0, avgCnt: 0 },
              unharvested: { lengthM: 0, kg: 0, avgSum: 0, avgCnt: 0 },
            });
          }
          if (!varietyByBayStatus.has(bay)) {
            varietyByBayStatus.set(bay, {
              harvested: new Set(),
              unharvested: new Set(),
            });
          }
          if (!firstSeenIndexByBayStatus.has(bay)) {
            firstSeenIndexByBayStatus.set(bay, {
              harvested: null,
              unharvested: null,
            });
          }
        };

        daily.forEach((it, idx) => {
          const bay = String(it.bayName);
          const rows = expandCompressedRows(it.rowName);

          if (!rowsByBay.has(bay)) rowsByBay.set(bay, new Set());
          if (!rowHarvestedByBay.has(bay))
            rowHarvestedByBay.set(bay, new Map());
          if (!rowLabelByBay.has(bay)) rowLabelByBay.set(bay, new Map());

          const rowSet = rowsByBay.get(bay);
          const rhMap = rowHarvestedByBay.get(bay);
          const rlMap = rowLabelByBay.get(bay);

          // per-row
          rows.forEach((r) => {
            rowSet.add(r);
            const prevH = rhMap.has(r) ? rhMap.get(r) : true;
            rhMap.set(r, prevH && !!it.isHarvested);

            // prefer item's rowLabel, normalized for the concrete row number
            const candidate = labelForRow(it.rowLabel, r);
            if (!rlMap.has(r) && candidate) rlMap.set(r, candidate);
          });

          // bay-level harvested (AND)
          const prevBayH = harvestedByBay.has(bay)
            ? harvestedByBay.get(bay)
            : true;
          harvestedByBay.set(bay, prevBayH && !!it.isHarvested);

          // variety
          if (!varietySetByBay.has(bay)) varietySetByBay.set(bay, new Set());
          if (it.variety) varietySetByBay.get(bay).add(String(it.variety));

          // metrics
          const len = Number(it.rowLength) || 0;
          const kg = yieldPerM > 0 ? len * yieldPerM : NaN;
          if (!accByBay.has(bay))
            accByBay.set(bay, { lengthM: 0, kg: 0, avgSum: 0, avgCnt: 0 });
          const acc = accByBay.get(bay);
          acc.lengthM += len;
          if (Number.isFinite(kg)) acc.kg += kg;
          const avg = Number(it.averageLeafLength);
          if (Number.isFinite(avg)) {
            acc.avgSum += avg;
            acc.avgCnt += 1;
          }

          ensureAccPair(bay);
          const statusKey = it.isHarvested ? "harvested" : "unharvested";

          const seen = firstSeenIndexByBayStatus.get(bay);
          if (seen[statusKey] == null) seen[statusKey] = idx;

          const acc2 = accByBayStatus.get(bay)[statusKey];
          acc2.lengthM += len;
          if (Number.isFinite(kg)) acc2.kg += kg;
          if (Number.isFinite(avg)) {
            acc2.avgSum += avg;
            acc2.avgCnt += 1;
          }
          if (it.variety)
            varietyByBayStatus.get(bay)[statusKey].add(String(it.variety));
        });

        const linesWithOrder = [];
        for (const [bay, set] of rowsByBay.entries()) {
          const rowListAll = Array.from(set).sort((a, b) => a - b);
          const rhMapAll = rowHarvestedByBay.get(bay) || new Map();

          const harvestedRows = rowListAll.filter((r) => !!rhMapAll.get(r));
          const unharvestedRows = rowListAll.filter((r) => !rhMapAll.get(r));

          const rlMap = rowLabelByBay.get(bay) || new Map();
          const accPair = accByBayStatus.get(bay) || {
            harvested: { lengthM: 0, kg: NaN, avgSum: 0, avgCnt: 0 },
            unharvested: { lengthM: 0, kg: NaN, avgSum: 0, avgCnt: 0 },
          };
          const varPair = varietyByBayStatus.get(bay) || {
            harvested: new Set(),
            unharvested: new Set(),
          };
          const firstSeen = firstSeenIndexByBayStatus.get(bay) || {
            harvested: null,
            unharvested: null,
          };

          const buildLine = (statusKey, rowsForLine) => {
            if (!rowsForLine.length) return null;

            const acc = accPair[statusKey] || {
              lengthM: 0,
              kg: NaN,
              avgSum: 0,
              avgCnt: 0,
            };
            const crates =
              Number.isFinite(acc.kg) && weightPerCrate > 0
                ? Math.ceil(acc.kg / weightPerCrate)
                : NaN;
            const avgLeafCm = acc.avgCnt > 0 ? acc.avgSum / acc.avgCnt : NaN;

            const compressed = compressRowIndices(rowsForLine);

            let suffix = "";
            if (showVariety) {
              const varSet = varPair[statusKey] || new Set();
              if (varSet.size === 1) suffix = ` (${Array.from(varSet)[0]})`;
            }

            return {
              text: `${greenHouseLabel} ${bay} ${blockLabel} ${compressed}${suffix}`,
              harvested: statusKey === "harvested",
              meta: {
                lengthM: acc.lengthM,
                kg: acc.kg,
                crates,
                avgLeafCm,
                greenhouseName: bay,
                rowStates: rowsForLine.map((r) => ({
                  rowLabel: rlMap.get(r) || `Row ${r}`,
                  harvested: !!rhMapAll.get(r),
                })),
              },
            };
          };

          // show harvested rows as their own merged line (green), and unharvested separately (black)
          const harvestedLine = buildLine("harvested", harvestedRows);
          if (harvestedLine) {
            linesWithOrder.push({
              order:
                firstSeen.harvested != null
                  ? firstSeen.harvested
                  : Number.MAX_SAFE_INTEGER,
              tie: segmentTieBreaker++,
              line: harvestedLine,
            });
          }

          const unharvestedLine = buildLine("unharvested", unharvestedRows);
          if (unharvestedLine) {
            linesWithOrder.push({
              order:
                firstSeen.unharvested != null
                  ? firstSeen.unharvested
                  : Number.MAX_SAFE_INTEGER,
              tie: segmentTieBreaker++,
              line: unharvestedLine,
            });
          }
        }

        // NEW: Keep merged+split lines in the same visual order as the original unmerged list
        linesWithOrder.sort((a, b) =>
          a.order !== b.order ? a.order - b.order : a.tie - b.tie,
        );

        result[dayInfo.iso] = linesWithOrder.map((x) => x.line);
      } else {
        // Split rows: each row separately
        const expanded = [];
        for (const it of daily) {
          const rows = expandCompressedRows(it.rowName);
          const varietySuffix =
            showVariety && it.variety ? ` (${it.variety})` : "";
          const len = Number(it.rowLength) || 0;
          const kg = yieldPerM > 0 ? len * yieldPerM : NaN;
          const crates =
            Number.isFinite(kg) && weightPerCrate > 0
              ? Math.ceil(kg / weightPerCrate)
              : NaN;
          const avgLeafCm = Number(it.averageLeafLength);

          if (!rows.length) {
            expanded.push({
              text: `${greenHouseLabel} ${it.bayName} ${it.rowLabel}${varietySuffix}`,
              harvested: !!it.isHarvested,
              meta: {
                lengthM: len,
                kg,
                crates,
                avgLeafCm: Number.isFinite(avgLeafCm) ? avgLeafCm : NaN,
                greenhouseName: it.bayName,
                rowStates: [
                  { rowLabel: it.rowLabel, harvested: !!it.isHarvested },
                ],
              },
            });
          } else {
            rows.forEach((rowNumber) => {
              expanded.push({
                text: `${greenHouseLabel} ${it.bayName} ${it.rowLabel}${varietySuffix}`,
                harvested: !!it.isHarvested,
                meta: {
                  lengthM: len,
                  kg,
                  crates,
                  avgLeafCm: Number.isFinite(avgLeafCm) ? avgLeafCm : NaN,
                  greenhouseName: it.bayName,
                  rowStates: [
                    { rowLabel: it.rowLabel, harvested: !!it.isHarvested },
                  ],
                },
              });
            });
          }
        }
        result[dayInfo.iso] = expanded;
      }
    }
    return result;
  }, [
    days,
    items,
    greenHouseLabel,
    blockLabel,
    mergeRows,
    showVariety,
    yieldPerM,
    weightPerCrate,
  ]);

  const maxRowCount = useMemo(
    () => Math.max(0, ...days.map((d) => cellValuesByDay[d.iso]?.length ?? 0)),
    [days, cellValuesByDay],
  );

  const requirementTextByISO = useMemo(() => {
    const out = new Map();
    for (const d of days) {
      const req = getRequirementForDay(visibleDaysConfig, d.dayName);
      out.set(d.iso, `Requirement: ${formatRequirementText(req)}`);
    }
    return out;
  }, [days, visibleDaysConfig]);

  const columns = useMemo(
    () =>
      days.map((d) => {
        // In demo mode, anything before the real UI date (same week) is forced green
        const sameWeekAsUI =
          demoMode &&
          uiDate &&
          startOfWeekMonday(dayjs(d.iso)).isSame(
            startOfWeekMonday(uiDate),
            "day",
          );
        const isBeforeUI = sameWeekAsUI && dayjs(d.iso).isBefore(uiDate, "day");

        return {
          title: (
            <Tooltip title={requirementTextByISO.get(d.iso)}>
              <span>{d.title}</span>
            </Tooltip>
          ),
          dataIndex: d.iso,
          key: d.iso,
          render: (value) => {
            if (!value) return <span className="text-darkerGray">—</span>;
            // value is { text, harvested } OR string (fallback)
            const obj =
              typeof value === "object" && value.text != null
                ? value
                : { text: String(value), harvested: false, meta: null };

            let style;
            if (isBeforeUI) {
              style = { color: COLORS.successGreen };
            } else if (obj.harvested) {
              style = { color: "black", fontWeight: "bold" };
            } else {
              const stageColor = getLeafStageColor(obj.meta?.avgLeafCm, minLeafCm, maxLeafCm);
              style = stageColor ? { color: stageColor } : undefined;
            }

            const {
              targets,
              harvestedTargets,
              hasAnyUnharvested,
              hasAnyHarvested,
              allHarvested,
            } = selectTargetsFromCell(obj.meta || {});

            const disable13 = allHarvested || !hasAnyUnharvested; // options 1–3
            const disableUnharvest = !hasAnyHarvested;

            const onQuick = async (kind) => {
              if (!hasAnyUnharvested) return;
              const { failed, total } = await markQuick({
                kind,
                targets,
                org: currentOrgId,
                siteId: currentSite?.id,
                cropId: currentCrop?.id,
              });
              if (failed === 0)
                message.success(
                  `Updated ${total} ${total === 1 ? "row" : "rows"}.`,
                );
              else
                message.error(`Updated ${total - failed}, failed ${failed}.`);
              onRefresh?.();
            };

            const openCustom = () => {
              if (!hasAnyUnharvested) return;
              setModalTargets(targets);
              setModalOpen(true);
            };

            const onUnharvest = async () => {
              if (!hasAnyHarvested) return;
              const { failed, total } = await unharvest({
                targets: harvestedTargets,
                org: currentOrgId,
                siteId: currentSite?.id,
                cropId: currentCrop?.id,
              });
              if (failed === 0)
                message.success(
                  `Unharvested ${total} ${total === 1 ? "row" : "rows"}.`,
                );
              else
                message.error(
                  `Unharvested ${total - failed}, failed ${failed}.`,
                );
              onRefresh?.();
            };

            const menuItems = [
              {
                key: "today",
                label: "Mark harvested today",
                disabled: disable13,
                onClick: () => onQuick("today"),
              },
              {
                key: "yesterday",
                label: "Mark harvested yesterday",
                disabled: disable13,
                onClick: () => onQuick("yesterday"),
              },
              {
                key: "another",
                label: "Mark harvested on another date",
                disabled: disable13,
                onClick: openCustom,
              },
              {
                key: "unharvest",
                label: "Mark unharvested",
                disabled: disableUnharvest,
                onClick: onUnharvest,
              },
            ];

            return (
              <div className="flex items-center justify-between gap-2">
                <Tooltip title={buildCellTooltip(obj.meta)}>
                  <Text style={style}>{obj.text}</Text>
                </Tooltip>
                <Dropdown trigger={["hover"]} menu={{ items: menuItems }}>
                  <MoreOutlined style={{ cursor: "pointer", opacity: 0.75 }} />
                </Dropdown>
              </div>
            );
          },
        };
      }),
    [
      days,
      demoMode,
      uiDate,
      requirementTextByISO,
      currentOrgId,
      currentSite,
      currentCrop,
      onRefresh,
      minLeafCm,
      maxLeafCm,
    ],
  );

  const dataSource = useMemo(() => {
    return Array.from({ length: maxRowCount }, (_, index) => {
      const row = { key: String(index) };
      for (const d of days) row[d.iso] = cellValuesByDay[d.iso]?.[index] || "";
      return row;
    });
  }, [days, cellValuesByDay, maxRowCount]);

  const headerLabel = useMemo(
    () => formatFullHeader(demoMode ? uiDate : selectedDate),
    [demoMode, uiDate, selectedDate],
  );

  return (
    <Card variant="outlined">
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Text strong>Today :</Text>
          <Text>{headerLabel}</Text>
          <DatePicker
            value={demoMode ? uiDate : selectedDate}
            onChange={(d) => {
              if (!d) return;
              if (demoMode) {
                setUiDate(d);
                onDateChange(startOfWeekMonday(d));
              } else {
                onDateChange(d);
              }
            }}
            allowClear={false}
            disabledDate={(current) => {
              if (!current) return false;

              const today = dayjs().startOf("day");
              const monday = startOfWeekMonday(today);
              const maxAllowed = monday.add(11, "day").endOf("day"); // next week Friday

              return current.isAfter(maxAllowed);
            }}
          />
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={showVariety}
            onChange={() => setShowVariety((prev) => !prev)}
            checkedChildren="Show variety"
            unCheckedChildren="Hide variety"
          />
          <Switch
            checked={mergeRows}
            onChange={() => setMergeRows((prev) => !prev)}
            checkedChildren={"Merge " + blockLabel}
            unCheckedChildren={"Split " + blockLabel}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={onRefresh}
            loading={refreshLoading}
          >
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <Alert type="error" message={error} showIcon className="mb-3" />
      ) : null}

      {!loading && maxRowCount === 0 ? (
        <Empty description="No recommendations for the selected range." />
      ) : (
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          loading={{ spinning: loading, indicator: <LoadingIcon /> }}
          size="small"
          bordered
          scroll={{ x: true }}
        />
      )}

      <HarvestUpdateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          setModalOpen(false);
          onRefresh?.();
        }}
        siteMetadata={siteMetadata}
        orgId={currentOrgId}
        siteId={currentSite?.id}
        cropId={currentCrop?.id}
        targets={modalTargets}
      />
    </Card>
  );
};

export default RecommendedScheduleView;
